// v2 fence — a post-and-plank paddock fence in weathered silver-grey timber:
// capped sawn posts, two wide flat boards per run, and a rising diagonal brace
// from the post foot up to the boundary, over a staked layout board while under
// construction. Ported 1:1 from age-of-mcp's v2/fence.ts, using the library's
// shared `roadwork` (armFrame) helper. The per-tile network params (connections /
// heights) are not modelled here, so this renders the default lone-post look the
// flattened file bakes (connections = 0 -> no connection arms): groundwork, then
// the central capped post rising through the build stages.
import * as THREE from "three";
import { defineModel, roadwork, type Part } from "../../lib";

// Weathered silver-grey paddock timber.
const POST_COLOR  = "#6f6557";
const PLANK_COLOR = "#90866f";
const BRACE_COLOR = "#7d7361";
const CAP_COLOR   = "#5f564a";

const STAGE_H = [0, 0.18, 0.30, 0.42];
const POST_W  = 0.075;
const PLANK_H = 0.085;   // wide flat board face
const PLANK_T = 0.028;
const PLANK_FRACS = [0.52, 0.88];   // board heights as fractions of the run

// A sawn post with a small cap board on top.
function cappedPost(x: number, y: number, z: number, postH: number, w: number): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(w, postH, w),
      color: POST_COLOR, position: [x, y + postH / 2, z] },
    { geometry: new THREE.BoxGeometry(w + 0.035, 0.024, w + 0.035),
      color: CAP_COLOR, position: [x, y + postH + 0.012, z] },
  ];
}

// Stage-0 groundwork: a staked-out layout board.
function groundwork(): Part[] {
  const parts: Part[] = [
    { geometry: new THREE.BoxGeometry(0.6, 0.035, 0.6),
      color: "#8a8170", position: [0, 0.0175, 0] },
  ];
  for (const [sx, sz] of [[-0.22, 0.22], [0.22, -0.22]] as const) {
    parts.push({
      geometry: new THREE.BoxGeometry(0.04, 0.12, 0.04),
      color: POST_COLOR, position: [sx, 0.06, sz],
    });
  }
  return parts;
}

// One half-arm per connected direction — two wide planks, a rising diagonal
// brace, and a boundary post for directions 0–3. With no connections this
// contributes nothing; kept verbatim so the fence's structure mirrors the
// source whenever a network supplies its mask.
function connectionArms(h: number, connections: number, heights: readonly number[]): Part[] {
  const has = (i: number): boolean => (connections & (1 << i)) !== 0;
  const parts: Part[] = [];
  const postH = h + 0.05;

  for (let i = 0; i < 8; i++) {
    if (!has(i)) continue;
    const diagonal = (i & 1) === 1;
    if (diagonal && (has((i + 7) % 8) || has((i + 1) % 8))) continue;

    const f = roadwork.armFrame(i, heights);
    const rot: [number, number, number] = [0, f.yaw, f.pitch];

    // Two wide flat boards — midpoint of the half-arm, halfway up the rise.
    for (const frac of PLANK_FRACS) {
      parts.push({
        geometry: new THREE.BoxGeometry(f.len3D, PLANK_H, PLANK_T),
        color: PLANK_COLOR,
        position: [f.dx * 0.25, h * frac + f.dY * 0.5, f.dz * 0.25],
        rotation: rot, eulerOrder: "YZX",
      });
    }

    // Rising diagonal brace: from low by the post up to high by the boundary.
    const t0 = 0.10, t1 = 0.90;
    const yA = h * 0.14 + f.dY * t0;
    const yB = h * 0.82 + f.dY * t1;
    const run = f.horiz * (t1 - t0);
    const bLen = Math.hypot(run, yB - yA);
    const bPitch = Math.atan2(yB - yA, run);
    parts.push({
      geometry: new THREE.BoxGeometry(bLen, 0.05, 0.024),
      color: BRACE_COLOR,
      position: [f.dx * 0.25, (yA + yB) / 2, f.dz * 0.25],
      rotation: [0, f.yaw, bPitch], eulerOrder: "YZX",
    });

    // Boundary post at the shared tile edge — drawn only for directions 0–3.
    if (i < 4) {
      parts.push(...cappedPost(f.dx * 0.5, f.dY, f.dz * 0.5, postH, 0.07));
    }
  }
  return parts;
}

function stage(s: number, connections = 0, heights: readonly number[] = []): Part[] {
  if (s <= 0) return groundwork();
  const h = STAGE_H[Math.min(s, 3)]!;
  if (h <= 0) return [];
  return [
    ...cappedPost(0, 0, 0, h + 0.05, POST_W),
    ...connectionArms(h, connections, heights),
  ];
}

export const fenceV2 = defineModel({
  id: "fence-v2",
  tiles: { w: 1, h: 1 },
  batchId: "fable",
  name: "Fence (v2)",
  tags: ["building","fence","low-poly","age-of-mcp","fable"],
  summary: "Low-poly fence.",
  description: "A low wooden fence panel that blocks passage.",
  author: "age-of-mcp",
  footprint: 0.8,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => stage(0) },
    { id: "stage-1", label: "Lower", parts: () => stage(1) },
    { id: "stage-2", label: "Upper", parts: () => stage(2) },
    { id: "complete", label: "Complete", parts: () => stage(3) },
  ],
  animations: [],
});
